import { InjectionToken, ModuleWithProviders, NgModule } from "@angular/core";
import { ExtraOptions, Route, RouterModule, ROUTES } from "@angular/router";

/**
 * Logic which is executed on the routes configurations before they being bootstrapped in runtime
 */
export interface RouteHook<T extends Route = Route> {
	/**
	 * priority. assuming that there can be multiple hooks, it's better to define order between them.
	 * Hook having prio of 0 is executed before hook with prio of 1
	 */
	prio: number;
	process: (routes: T[]) => T[];
}

/**
 * token to registrer hooks on all levels (root and lazy)
 */
export const ROUTE_HOOK = new InjectionToken<Route>("ROUTE_HOOK");

/**
 * alternative way to provide `Routes` instead of passing to `forRoot`/`forChild` directly
 * can be used to calculate routes in a factory
 *
 * @example
 * { provide: HOOKABLE_ROUTES, useFactory: (config) => config.isDesktop ? desktopRoutes : mobileRoutes, deps: [CONFIG] }
 */
export const HOOKABLE_ROUTES = new InjectionToken<Route[]>("HOOKABLE_ROUTES");

const voidHook: RouteHook = {
	prio: 0,
	process: (x) => x,
};

/**
 * wraps angular RouterModule to provide generic hooks for route transformation.
 * route transformation function can be provided in `ROUTE_HOOK` as multi provider
 */
@NgModule()
export class HookableRouterModule {
	static forRoot(routes?: Route[], config?: ExtraOptions): ModuleWithProviders<RouterModule> {
		return {
			ngModule: RouterModule,
			providers: [
				...(RouterModule.forRoot([], config).providers ?? []),
				{
					provide: ROUTES,
					multi: true,
					useFactory: factory.bind(undefined, routes),
					deps: [ROUTE_HOOK, HOOKABLE_ROUTES],
				},
				{
					provide: ROUTE_HOOK,
					useValue: voidHook,
					multi: true,
				},
				{
					provide: HOOKABLE_ROUTES,
					useValue: [],
				},
			],
		};
	}

	// must be done for every child as RouterModule.forChild provides new `ROUTES` there
	static forChild(routes?: Route[]): ModuleWithProviders<RouterModule> {
		return {
			ngModule: RouterModule,
			providers: [
				...(RouterModule.forChild([]).providers ?? []),
				{
					provide: ROUTES,
					multi: true,
					useFactory: factory.bind(undefined, routes),
					deps: [ROUTE_HOOK, HOOKABLE_ROUTES],
				},
			],
		};
	}
}

export function factory(routes: Route[], hooks: RouteHook[], routesFromToken: Route[] = []) {
	routes = routes || routesFromToken;

	// shallow copy of route config is required for server cos on server side
	// same route config objects in memory reused for multiple requests.
	// without shallow copy they got corrupted after first request
	return processRoutes(
		routes.map((r) => ({ ...r })),
		...hooks
	);
}

export function processRoutes(initialRoutes: Route[], ...hooks: RouteHook[]) {
	const sorted = hooks.sort((h1, h2) => h1.prio - h2.prio);
	return sorted.reduce((routes, hook) => hook.process(routes), initialRoutes);
}
